<template>
  <Transition name="fade-slide" appear>
    <div
      v-if="isVisible"
      class="custom-inline-alert rounded px-3 max-w-full border-l-[3px]"
      :class="[`bg-color-${safeColor}`, `size-${size}`, sizeClassMap[size].padding]"
      :style="{ borderLeftColor: `var(--color-${safeColor})` }"
      :role="defaultRole"
    >
      <div class="row-center alert-content gap-3">
        <i
          v-if="isTablerIcon && icon"
          :class="[`ti ti-${icon} color-${safeColor}`, sizeClassMap[size].icon]"
          aria-hidden="true"
        ></i>
        <CustomImage v-else-if="icon" :src="getIconUrl(icon)" alt="" class="alert-img" />
        <div class="flex-1 min-w-0">
          <div v-if="title" class="font-semibold color-text mb-1" :class="sizeClassMap[size].title">
            {{ title }}
          </div>
          <div class="color-text" :class="sizeClassMap[size].message">{{ message }}</div>
        </div>
        <CustomIonButton
          v-if="dismissable"
          fill="clear"
          :color="safeColor"
          size="small"
          ariaLabel="Dismiss"
          @onClick="onAlertDismissed"
        >
          <i class="ti ti-x text-m" aria-hidden="true"></i>
        </CustomIonButton>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
import CustomImage from "@/components/primitives/CustomImage.vue";
import CustomIonButton from "@/components/primitives/CustomIonButton.vue";
import { getIconUrl } from "@/methods/utils";
import { ComponentColorType, ComponentSizeType } from "@/types/componentType";
import { PropType, computed, defineComponent, ref } from "vue";

const ALERT_COLORS: ReadonlyArray<ComponentColorType> = [
  "primary",
  "tertiary",
  "secondary",
  "success",
  "danger",
  "warning",
  "medium",
];

export default defineComponent({
  components: { CustomImage, CustomIonButton },
  name: "CustomInlineAlert",
  props: {
    title: { type: String, default: null },
    message: { type: String, required: true },
    color: { type: String as PropType<ComponentColorType>, default: "tertiary" },
    size: { type: String as PropType<ComponentSizeType>, default: "default" },
    icon: { type: String, default: null },
    dismissable: { type: Boolean, default: false },
    isTablerIcon: { type: Boolean, default: true },
  },
  setup(props, { emit }) {
    const isVisible = ref(true);

    // `color` can arrive from an API payload (UserAlert, QuestionEvaluation). An unrecognised
    // value would otherwise emit `bg-color-{garbage}` and paint no background, border or icon.
    const safeColor = computed<ComponentColorType>(() =>
      ALERT_COLORS.includes(props.color) ? props.color : "tertiary",
    );

    // Callers may still override `role` — a fallthrough attr wins over this binding.
    const defaultRole = computed(() => (safeColor.value === "danger" ? "alert" : "status"));

    const sizeClassMap: Record<
      ComponentSizeType,
      { padding: string; title: string; message: string; icon: string }
    > = {
      small: { padding: "py-1", title: "text-xxxs", message: "text-xxs", icon: "text-xl" },
      default: { padding: "py-2", title: "text-xxs", message: "text-xs", icon: "text-xxl" },
      large: { padding: "py-3", title: "text-m", message: "text-s", icon: "text-xxl" },
    };

    function onAlertDismissed() {
      isVisible.value = false;
      emit("onDismissed");
    }
    return { getIconUrl, onAlertDismissed, isVisible, sizeClassMap, safeColor, defaultRole };
  },
});
</script>

<style lang="scss" scoped>
// scoped-defer: bucket-c-candidate: nested SCSS rules — child-element gluing, hoist to @layer components if reused
.custom-inline-alert {
  --icon-size: 32px;
  &.size-small {
    --icon-size: 24px;
  }
  &.size-large {
    --icon-size: 48px;
  }
  & .alert-content {
    & .alert-img {
      width: var(--icon-size);
      height: var(--icon-size);
    }
  }
}
// scoped-defer: bucket-d-complex-selector: complex selector ".fade-slide-enter-active, .fade-slide-leave-active"
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
}
// scoped-defer: bucket-d-complex-selector: complex selector ".fade-slide-enter-from, .fade-slide-leave-to"
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
